import { inject, Injectable } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { ColorMetadataService } from '@ddv/charts';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import {
    AppWidgetState,
    BaseConfigItem,
    ChartConfigItem,
    Fund,
    GridConfigItem,
    LegacyAppWidgetState,
    LegacyWidgetSettings,
    MirroredValueConfigItem,
    MODE,
    Sharing,
    SlicerConfigItem,
    TooltipConfigItem,
    UserPermission,
    VisualizationConfigs,
    WidgetSettings,
    WidgetSnapshot,
} from '@ddv/models';
import { FundsService } from '@ddv/reference-data';
import { deepClone } from '@ddv/utils';
import { SelectedWidgetRelayService } from '@ddv/visualizations';
import { Theme, ThemeService } from '@hs/ui-core-presentation';
import { combineLatest, map, Observable } from 'rxjs';

@Injectable()
export class LegacyWidgetsService {
    private readonly ddvApiService = inject<ApiExecutorService>(ApiServices.ddvMW);
    private readonly currentStateService = inject(CurrentStateService);
    private readonly fundsService = inject(FundsService);
    private readonly relay = inject(SelectedWidgetRelayService);
    private readonly themeService = inject(ThemeService);

    private clientCode = '';
    private theme: Theme = Theme.light;

    constructor() {
        this.currentStateService.clientCode$.subscribe((clientCode: string) => this.clientCode = clientCode);
        this.themeService.currentTheme$.subscribe((theme: Theme) => this.theme = theme);
    }

    fetchWidget(id: number): Observable<LegacyAppWidgetState> {
        return this.ddvApiService.invokeServiceWithParams(this.clientCode, `widgets/${id}`);
    }

    fetchWidgetInfo(id: number): Observable<AppWidgetState> {
        return combineLatest([this.fetchWidget(id), this.fundsService.funds()])
            .pipe(map(([widget, funds]) => {
                this.updateIsWidgetGlobal(!!widget.isGlobal);
                const updatedWidget = this.getUpdatedWidget(widget);
                return this.mapWidgetState(updatedWidget, funds);
            }));
    }

    fetchAllWidgets(): Observable<WidgetSnapshot[]> {
        return this.ddvApiService.invokeServiceWithParams(this.clientCode, 'widgets/all');
    }

    removeWidget(widgetId: number): Observable<void> {
        return this.ddvApiService.invokeServiceWithBody(this.clientCode, `widgets/${widgetId}`, 'DELETE', null);
    }

    fetchWidgetInfoForMultiClient(id: number): Observable<AppWidgetState> {
        return this.fetchWidget(id)
            .pipe(map((widget) => {
                this.updateIsWidgetGlobal(!!widget.isGlobal);
                const updatedWidget = this.getUpdatedWidget(widget);
                return this.mapWidgetState(updatedWidget, []);
            }));
    }

    saveCoreWidget(config: AppWidgetState, isMultiClient: boolean): Observable<AppWidgetState> {
        const { sharing, widgetSettings, ...rest } = config;
        const widgetPermissions = toPermissions(sharing);
        const legacyWidgetSettings = getLegacyWidgetSettings(widgetSettings);
        const payload = deepClone({ ...rest, widgetPermissions, widgetSettings: legacyWidgetSettings });

        const { widgetFilters } = rest;

        if (payload.widgetFilters && widgetFilters) {
            (payload.widgetFilters.funds as { fundId: string, fundName: string }[] | undefined) = widgetFilters
                .funds?.map((f) => ({ fundId: f.fundId, fundName: f.fundName }));
        }

        const method = rest.id ? 'PUT' : 'POST';

        if (isMultiClient) {
            return this.ddvApiService.invokeServiceWithBody<Partial<LegacyAppWidgetState>>(this.clientCode, 'widgets', method, payload)
                .pipe(map((widget) => {
                    const updatedWidget = this.getUpdatedWidget(widget);
                    return new AppWidgetState(updatedWidget);
                }));
        }

        return combineLatest([
            this.ddvApiService.invokeServiceWithBody<Partial<LegacyAppWidgetState>>(this.clientCode, 'widgets', method, payload),
            this.fundsService.funds(),
        ]).pipe(map(([widget, funds]) => {
            const updatedWidget = this.getUpdatedWidget(widget);
            return new AppWidgetState(updatedWidget, funds);
        }));
    }

    validateWidgetName(name: string): Observable<boolean> {
        return this.ddvApiService.invokeServiceWithParams(this.clientCode, 'widgets/isNameValid', { name: encodeURIComponent(name) });
    }

    updateIsWidgetGlobal(isWidgetGlobal: boolean): void {
        this.relay.relayIsWidgetGlobal(isWidgetGlobal);
    }

    clearAllFilters(widgetId: number): void {
        this.relay.clearAllFilters(widgetId);
    }

    private mapWidgetState(widget: Partial<AppWidgetState>, funds: Fund[]): AppWidgetState {
        this.mapWidgetVizConfigs(widget, this.theme);
        return new AppWidgetState(widget, funds);
    }

    private mapWidgetVizConfigs(widget: Partial<AppWidgetState>, theme: Theme): void {
        const advancedGridVizConfig = widget.visualizationConfigs?.find((vc: VisualizationConfigs) => vc.visualizationType === 'ADVANCED_GRID');
        if (advancedGridVizConfig) {
            this.mapGridConfigColumnConditionColors(advancedGridVizConfig.configs?.values as GridConfigItem[] ?? [], theme);
        }
        const simpleGridVizConfig = widget.visualizationConfigs?.find((vc: VisualizationConfigs) => vc.visualizationType === 'SIMPLE_GRID');
        if (simpleGridVizConfig) {
            this.mapGridConfigColumnConditionColors(simpleGridVizConfig.configs?.values as GridConfigItem[] ?? [], theme);
        }

        widget.visualizationConfigs?.forEach((vc: VisualizationConfigs) => {
            if (vc.visualizationType === 'LINE_CHART') {
                if (vc.configs?.slicers?.length) {
                    this.mapLineChartConfigCustomStyles(vc.configs.slicers);
                } else if (vc.configs?.values?.length) {
                    this.mapLineChartConfigValueColorNames(vc.configs.values as ChartConfigItem[]);
                }
            }
        });
    }

    private mapGridConfigColumnConditionColors(values: GridConfigItem[], theme: Theme): void {
        values.forEach((v) => {
            if (v.columnCondition?.length) {
                v.columnCondition.forEach((colCondition) => {
                    ColorMetadataService.mapThemeColors(theme, colCondition);
                });
            }
        });
    }

    private mapLineChartConfigCustomStyles(values: SlicerConfigItem[]): void {
        values.forEach((v) => {
            v.configCustomStyles = v.configCustomStyles?.filter((attribute) => {
                return !ColorMetadataService.isLineChartConfigCustomStyleOutdated(attribute.attributeColorName);
            });
        });
    }

    private mapLineChartConfigValueColorNames(values: ChartConfigItem[]): void {
        values.forEach((value, index) => {
            value.colorName = ColorMetadataService.mapOutdatedLineChartValueColorName(value.colorName ?? '', index);
        });
    }

    // This converts the widget coming from the MW to a new widget by stripping any extra properties and changing permissions to sharing
    private getUpdatedWidget(widget: Partial<LegacyAppWidgetState>): AppWidgetState {
        const { widgetPermissions, widgetSettings, ...rest } = widget;
        const updatedWidgetSettings = getWidgetSettings(widgetSettings);
        const updatedWidget = new AppWidgetState(
            {
                ...rest,
                widgetSettings: updatedWidgetSettings,
                sharing: fromPermissions(widgetPermissions ?? []),
            });
        this.updateWidgetConfigs(updatedWidget);
        return updatedWidget;
    }

    // This converts config items coming from the MW to new config items by stripping any extra properties
    private updateWidgetConfigs(widget: AppWidgetState): void {
        if (widget.coreWidgetType === 'VISUALIZATION') {
            widget.visualizationConfigs.forEach((vizConfig) => {
                if (vizConfig.configs) {
                    const { axisTimes, details, mirroredValues, slicers, tooltips, values } = vizConfig.configs;

                    if (axisTimes) {
                        vizConfig.configs.axisTimes = axisTimes.map((at) => {
                            return getBaseConfigItem(at);
                        });
                    }

                    if (details) {
                        vizConfig.configs.details = details.map((dc) => {
                            return getBaseConfigItem(dc);
                        });
                    }

                    if (mirroredValues) {
                        vizConfig.configs.mirroredValues = mirroredValues.map((mvc) => {
                            return getMirroredValueConfigItem(mvc);
                        });
                    }

                    if (slicers) {
                        vizConfig.configs.slicers = slicers.map((sc) => {
                            return getSlicerConfigItem(sc);
                        });
                    }

                    if (tooltips) {
                        vizConfig.configs.tooltips = tooltips.map((tc) => {
                            return getTooltipConfigItem(tc);
                        });
                    }

                    if (values) {
                        if (vizConfig.visualizationType === 'ADVANCED_GRID' || vizConfig.visualizationType === 'SIMPLE_GRID') {
                            vizConfig.configs.values = values.map((vc) => {
                                return getGridConfigItem(vc as GridConfigItem);
                            });
                        } else {
                            vizConfig.configs.values = values.map((vc) => {
                                return getChartConfigItem(vc as ChartConfigItem);
                            });
                        }
                    }
                }
            });
        }
    }
}

// This converts the permissions coming from the MW to the new sharing type
function fromPermissions(permissions: UserPermission[]): Sharing {
    if (!permissions) {
        return { toUsers: [], toTeams: [] };
    }

    return {
        toUsers: permissions
            .filter((p) => p.sharedType === 'USER')
            .map((p) => ({ name: p.name, permission: p.privilege === 'EDIT' ? 'write' : 'read' })),
        toTeams: permissions
            .filter((p) => p.sharedType === 'TEAM')
            .map((p) => ({ name: p.name, permission: p.privilege === 'EDIT' ? 'write' : 'read' })),
    };
}

// This converts the new sharing type to permissions to be sent to the MW
function toPermissions(sharing: Sharing): UserPermission[] {
    const users: UserPermission[] = sharing.toUsers.map((user) => ({
        name: user.name,
        privilege: user.permission === 'write' ? 'EDIT' : 'VIEW',
        sharedType: 'USER',
    }));
    const teams: UserPermission[] = sharing.toTeams.map((team) => ({
        name: team.name,
        privilege: team.permission === 'write' ? 'EDIT' : 'VIEW',
        sharedType: 'TEAM',
    }));
    return [...users, ...teams];
}

function getLegacyWidgetSettings(widgetSettings: WidgetSettings | undefined): LegacyWidgetSettings[] {
    return [
        {
            mode: MODE.EDIT_WORKSPACE,
            isSubscribedToDashboardFilters: widgetSettings?.isSubscribedToDashboardFilters,
            isActiveDate: widgetSettings?.isActiveDate,
        },
        {
            mode: MODE.VIEW,
            isSubscribedToDashboardFilters: widgetSettings?.isSubscribedToDashboardFilters,
            isActiveDate: widgetSettings?.isActiveDate,
        },
    ];
}

function getWidgetSettings(legacyWidgetSettings: LegacyWidgetSettings[] | undefined): WidgetSettings {
    return {
        isSubscribedToDashboardFilters: !!legacyWidgetSettings?.[0]?.isSubscribedToDashboardFilters,
        isActiveDate: !!legacyWidgetSettings?.[0]?.isActiveDate,
    };
}

function getMirroredValueConfigItem(configItem: MirroredValueConfigItem): MirroredValueConfigItem {
    return {
        ...getBaseConfigItem(configItem),
        aggregationType: configItem.aggregationType,
        colorType: configItem.colorType,
        colorName: configItem.colorName,
        colorSortBy: configItem.colorSortBy,
        colorSortDirection: configItem.colorSortDirection,
        configCustomStyles: configItem.configCustomStyles,
    };
}

function getSlicerConfigItem(configItem: SlicerConfigItem): SlicerConfigItem {
    return {
        ...getBaseConfigItem(configItem),
        isDefault: !!configItem.isDefault,
        configCustomSortOrder: configItem.configCustomSortOrder,
        colorType: configItem.colorType,
        colorName: configItem.colorName,
        colorSortBy: configItem.colorSortBy,
        colorSortDirection: configItem.colorSortDirection,
        configCustomStyles: configItem.configCustomStyles,
    };
}

function getTooltipConfigItem(configItem: TooltipConfigItem): TooltipConfigItem {
    return {
        ...getBaseConfigItem(configItem),
        aggregationType: configItem.aggregationType,
        numberFormat: configItem.numberFormat,
        decimalPlaces: configItem.decimalPlaces,
    };
}

// eslint-disable-next-line complexity
function getGridConfigItem(configItem: GridConfigItem): GridConfigItem {
    return {
        ...getBaseConfigItem(configItem),
        alignment: configItem.alignment ?? 'left',
        isHidden: !!configItem.isHidden,
        isRanked: !!configItem.isRanked,
        isBlank: !!configItem.isBlank,
        useAbsVal: !!configItem.useAbsVal,
        pinned: configItem.pinned ?? null,
        fontStyle: configItem.fontStyle,
        linkBehavior: configItem.linkBehavior ?? null,
        linkType: configItem.linkType ?? null,
        width: configItem.width ?? 100,
        customWidthWhenGroup: configItem.customWidthWhenGroup ?? null,
        pivotIndex: configItem.pivotIndex ?? null,
        rowGroupIndex: configItem.rowGroupIndex ?? null,
        agFilter: configItem.agFilter ?? null,
        agSort: configItem.agSort ? { sort: configItem.agSort.sort ?? null } : null,
        agSortIndex: configItem.agSortIndex ?? null,
        columnCondition: configItem.columnCondition ?? [],
        aggregationType: configItem.aggregationType ?? undefined,
        numberFormat: configItem.numberFormat ?? undefined,
        decimalPlaces: configItem.decimalPlaces ?? undefined,
        negativeValue: configItem.negativeValue ?? undefined,
        dateFormat: configItem.dateFormat ?? undefined,
        rankOrder: configItem.rankOrder ?? undefined,
        hyperlink: configItem.hyperlink ?? undefined,
        ddvWidget: configItem.ddvWidget ?? undefined,
    };
}

function getChartConfigItem(configItem: ChartConfigItem): ChartConfigItem {
    return {
        ...getBaseConfigItem(configItem),
        aggregationType: configItem.aggregationType,
        colorType: configItem.colorType,
        colorName: configItem.colorName,
        colorSortBy: configItem.colorSortBy,
        colorSortDirection: configItem.colorSortDirection,
        configCustomStyles: configItem.configCustomStyles,
    };
}

function getBaseConfigItem(configItem: BaseConfigItem): BaseConfigItem {
    return {
        colId: configItem.colId,
        label: configItem.label,
        value: configItem.value,
        datatype: configItem.datatype,
        displayType: configItem.displayType,
        showCustomName: !!configItem.showCustomName,
        customName: configItem.customName,
    };
}
